// 종목 관리 화면 (tb_stock_master)
// 상단: 조회 조건 / 좌측: 종목 목록 그리드 / 우측: 등록·수정 폼 (가로 레이아웃)
import React, { useState, useEffect, useCallback } from 'react';
import { fetchStocks, createStock, updateStock, deleteStock } from '../../api/stocks';
import { fetchCodesByType } from '../../api/codes';
import { getUsers } from '../../api/users';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import AlertBellStock from '../../components/common/AlertBellStock';
import { handleTickerClick } from '../../utils/naverChart';
import './StockMasterPage.css';

const EMPTY_FORM = {
  ticker: '',
  stock_name: '',
  sector_code_id: '',
  term_code_id: '',
  market_code_id: 'KR',
  price_code_id: 'AUTO',
  stock_code_id: 'STOCK',
  target_profit_rate: '',
  sort_order: 0,
  is_active: true,
  is_benchmark: false,
  compare_type: '',
  compare_ticker: '',
  compare_index_field: '',
};

const STOCK_TYPE_LABEL = { STOCK: '일반종목', DEPOSIT: '예금/적금', CMA: 'CMA', ETC: '기타' };
const PRICE_TYPE_LABEL = { AUTO: '자동', MANUAL: '수동' };

// 비교대상 - 지수 선택 옵션 (tb_market_index 컬럼명 기준)
const COMPARE_INDEX_OPTIONS = [
  { value: 'kospi',     label: '코스피'    },
  { value: 'kosdaq',    label: '코스닥'    },
  { value: 'sp500',     label: 'S&P500'    },
  { value: 'nasdaq100', label: '나스닥100' },
  { value: 'gold',      label: '금'        },
];

// 좌측 그리드 "비교대상" 컬럼 표시용 — 지수 라벨(gold는 그리드에서만 '금시세'로 표시)
const COMPARE_INDEX_GRID_LABEL = { kospi: '코스피', kosdaq: '코스닥', sp500: 'S&P500', nasdaq100: '나스닥100', gold: '금시세' };
const compareDisplay = (stock) => {
  if (stock.compare_type === 'STOCK') return stock.compare_ticker || '-';
  if (stock.compare_type === 'INDEX') return COMPARE_INDEX_GRID_LABEL[stock.compare_index_field] || stock.compare_index_field || '-';
  return '-';
};

const StockMasterPage = () => {
  const { user, isAdmin } = useAuth();

  const [stocks,    setStocks]    = useState([]);
  const [form,      setForm]      = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [message,   setMessage]   = useState({ text: '', type: '' });

  const [sectorCodes,   setSectorCodes]   = useState([]);
  const [termTypeCodes, setTermTypeCodes] = useState([]);

  // 관리자 전용: 사용자 목록 및 선택된 사용자 (기본: 본인)
  const [userList,       setUserList]       = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');

  const [filterMarket,    setFilterMarket]    = useState('');
  const [filterStockType, setFilterStockType] = useState('');
  const [filterActive,    setFilterActive]    = useState('');

  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 3000);
  };

  // 공통 코드 로드 (관리자 본인 코드 기준)
  useEffect(() => {
    fetchCodesByType('SECTOR').then(setSectorCodes).catch(() => {});
    fetchCodesByType('TERM_TYPE').then(setTermTypeCodes).catch(() => {});
  }, []);

  // 현재 로그인 사용자로 selectedUserId 초기화 (user 로드 후)
  useEffect(() => {
    if (user?.user_id && !selectedUserId) {
      setSelectedUserId(user.user_id);
    }
  }, [user, selectedUserId]);

  // 관리자: 사용자 목록 로드 (is_active=true, system 제외, user_name 오름차순)
  useEffect(() => {
    if (!isAdmin) return;
    getUsers()
      .then(res => {
        const list = (res.data || [])
          .filter(u => u.is_active && u.user_id !== 'system')
          .sort((a, b) => a.user_name.localeCompare(b.user_name, 'ko'));
        setUserList(list);
      })
      .catch(() => {});
  }, [isAdmin]);

  const loadStocks = useCallback(async () => {
    try {
      const params = {};
      if (filterMarket)        params.market_type = filterMarket;
      if (filterStockType)     params.stock_type  = filterStockType;
      if (filterActive !== '') params.is_active   = filterActive === 'true';
      // 관리자: 선택된 사용자 ID 전달
      if (isAdmin && selectedUserId) params.user_id = selectedUserId;
      const data = await fetchStocks(params);
      setStocks(data);
    } catch {
      showMessage('목록 조회에 실패했습니다.', 'error');
    }
  }, [filterMarket, filterStockType, filterActive, isAdmin, selectedUserId]);

  useEffect(() => { loadStocks(); }, [loadStocks]);

  // 관리자가 사용자를 변경하면 폼 초기화
  const handleUserChange = (userId) => {
    setSelectedUserId(userId);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const handleRowClick = (stock) => {
    setEditingId(stock.id);
    setForm({
      ticker:             stock.ticker,
      stock_name:         stock.stock_name,
      sector_code_id:     stock.sector_code_id || '',
      term_code_id:       stock.term_code_id   || '',
      market_code_id:     stock.market_code_id || 'KR',
      price_code_id:      stock.price_code_id  || 'AUTO',
      stock_code_id:      stock.stock_code_id  || 'STOCK',
      target_profit_rate: stock.target_profit_rate ?? '',
      sort_order:         stock.sort_order ?? 0,
      is_active:          stock.is_active,
      is_benchmark:       stock.is_benchmark ?? false,
      compare_type:         stock.compare_type        || '',
      compare_ticker:       stock.compare_ticker       || '',
      compare_index_field:  stock.compare_index_field  || '',
    });
  };

  const handleNew = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => {
      const next = {
        ...prev,
        [name]: type === 'checkbox' ? checked
               : type === 'number'  ? (value === '' ? '' : Number(value))
               : value,
      };
      if (name === 'price_code_id' && value === 'MANUAL') {
        next.market_code_id = '';
      }
      if (name === 'price_code_id' && value === 'AUTO' && !next.market_code_id) {
        next.market_code_id = 'KR';
      }
      // 비교대상 유형 전환 시 반대 필드 초기화 (STOCK↔INDEX 값 혼재 방지)
      if (name === 'compare_type') {
        next.compare_ticker = '';
        next.compare_index_field = '';
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (!form.ticker.trim())     { showMessage('티커를 입력해주세요.', 'error');   return; }
    if (!form.stock_name.trim()) { showMessage('종목명을 입력해주세요.', 'error'); return; }
    if (form.price_code_id === 'AUTO' && !form.market_code_id) {
      showMessage('자동수집 종목은 시장구분을 선택해주세요.', 'error');
      return;
    }

    const payload = {
      ...form,
      target_profit_rate: form.target_profit_rate === '' ? null : Number(form.target_profit_rate),
    };

    setLoading(true);
    try {
      if (editingId === null) {
        // 신규: 관리자는 선택된 사용자 ID로 등록
        const targetId = isAdmin && selectedUserId !== user?.user_id ? selectedUserId : null;
        await createStock(payload, targetId);
        showMessage('종목이 등록되었습니다.');
      } else {
        await updateStock(editingId, payload);
        showMessage('종목이 수정되었습니다.');
      }
      await loadStocks();
      handleNew();
    } catch (err) {
      showMessage(err.response?.data?.detail || '저장에 실패했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, stockName, e) => {
    e.stopPropagation();
    if (!window.confirm(`"${stockName}" 종목을 삭제하시겠습니까?`)) return;
    setLoading(true);
    try {
      await deleteStock(id);
      showMessage('종목이 삭제되었습니다.');
      await loadStocks();
      if (editingId === id) handleNew();
    } catch (err) {
      showMessage(err.response?.data?.detail || '삭제에 실패했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const toolbar = (
    <div className="stock-toolbar">
      <select value={filterMarket} onChange={(e) => setFilterMarket(e.target.value)}>
        <option value="">시장구분 전체</option>
        <option value="KR">KR</option>
        <option value="US">US</option>
      </select>
      <select value={filterStockType} onChange={(e) => setFilterStockType(e.target.value)}>
        <option value="">종목유형 전체</option>
        <option value="STOCK">일반종목</option>
        <option value="DEPOSIT">예금/적금</option>
        <option value="CMA">CMA</option>
        <option value="ETC">기타</option>
      </select>
      <select value={filterActive} onChange={(e) => setFilterActive(e.target.value)}>
        <option value="">사용유무 전체</option>
        <option value="true">사용</option>
        <option value="false">미사용</option>
      </select>
      {isAdmin && (
        <select
          className="stock-user-select"
          value={selectedUserId}
          onChange={(e) => handleUserChange(e.target.value)}
        >
          {userList.map(u => (
            <option key={u.user_id} value={u.user_id}>
              {u.user_name} ({u.user_id})
            </option>
          ))}
        </select>
      )}
    </div>
  );

  return (
    <PageLayout title="종목 관리" toolbar={toolbar} message={message}>
      <div className="stock-body">

        {/* ── 좌측: 종목 목록 그리드 ── */}
        <div className="stock-list-panel">
          <div className="grid-wrapper">
            <table className="grid-table">
              <thead>
                <tr>
                  <th className="col-center" style={{ width: 44 }}>정렬</th>
                  <th className="col-center" style={{ width: 50 }}>장단기</th>
                  <th style={{ width: 90 }}>섹터</th>
                  <th style={{ width: 160 }}>종목명</th>
                  <th style={{ width: 75 }}>티커</th>
                  <th className="col-center" style={{ width: 50 }}>시장구분</th>
                  <th className="col-center" style={{ width: 66 }}>종목유형</th>
                  <th className="col-center" style={{ width: 50 }}>시세유형</th>
                  <th className="col-center" style={{ width: 70 }}>목표수익률</th>
                  <th className="col-center" style={{ width: 54 }}>사용</th>
                  <th className="col-center" style={{ width: 54 }}>지표</th>
                  <th className="col-center" style={{ width: 80 }}>비교대상</th>
                  <th className="col-center" style={{ width: 50 }}>삭제</th>
                </tr>
              </thead>
              <tbody>
                {stocks.length === 0 ? (
                  <tr><td colSpan={13} className="grid-empty">데이터가 없습니다.</td></tr>
                ) : (
                  stocks.map((stock) => (
                    <tr
                      key={stock.id}
                      onClick={() => handleRowClick(stock)}
                      className={editingId === stock.id ? 'selected' : ''}
                    >
                      <td className="col-center">{stock.sort_order}</td>
                      <td className="col-center">{stock.term_type || stock.term_code_id}</td>
                      <td className="col-ellipsis">{stock.sector_name || stock.sector_code_id || ''}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span>{stock.stock_name}</span>
                          <AlertBellStock ticker={stock.ticker} stockName={stock.stock_name} />
                        </div>
                      </td>
                      <td
                        onClick={(e) => { e.stopPropagation(); handleTickerClick(stock.ticker, stock.market_code_id); }}
                        style={{
                          color:          stock.ticker !== '현금잔액' ? '#1A73E8' : '#333333',
                          cursor:         stock.ticker !== '현금잔액' ? 'pointer' : 'default',
                          textDecoration: stock.ticker !== '현금잔액' ? 'underline' : 'none',
                        }}
                        title={stock.ticker !== '현금잔액' ? '네이버 증권 차트 보기' : ''}
                      >
                        {stock.ticker}
                      </td>
                      <td className="col-center">{stock.market_code_id}</td>
                      <td className="col-center">{STOCK_TYPE_LABEL[stock.stock_code_id] || stock.stock_code_id}</td>
                      <td className="col-center">{PRICE_TYPE_LABEL[stock.price_code_id] || stock.price_code_id}</td>
                      <td className="col-center">
                        {stock.target_profit_rate != null ? `${stock.target_profit_rate}%` : ''}
                      </td>
                      <td className="col-center">
                        <span className={stock.is_active ? 'badge-active' : 'badge-inactive'}>
                          {stock.is_active ? '사용' : '미사용'}
                        </span>
                      </td>
                      <td className="col-center">
                        {stock.is_benchmark && <span className="badge-benchmark">지표</span>}
                      </td>
                      <td className="col-center col-ellipsis">{compareDisplay(stock)}</td>
                      <td className="col-center">
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={(e) => handleDelete(stock.id, stock.stock_name, e)}
                          disabled={loading}
                        >
                          삭제
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── 우측: 등록·수정 폼 (가로 레이아웃) ── */}
        <div className="stock-form-panel">
          <h3 className="stock-form-title">
            {editingId === null ? '신규 등록' : '종목 수정'}
            {isAdmin && selectedUserId && selectedUserId !== user?.user_id && (
              <span className="stock-form-user-badge"> ({selectedUserId})</span>
            )}
          </h3>

          <div className="sf-row">
            <label>종목명 <span className="required">*</span></label>
            <input
              type="text" name="stock_name" value={form.stock_name}
              onChange={handleFormChange} maxLength={200}
              placeholder="예) TIGER 미국S&P500"
            />
          </div>

          <div className="sf-row">
            <label>티커 <span className="required">*</span></label>
            <input
              type="text" name="ticker" value={form.ticker}
              onChange={handleFormChange} maxLength={20}
              placeholder="예) 360750, SCHD"
            />
          </div>

          <div className="sf-row">
            <label>섹터</label>
            <select name="sector_code_id" value={form.sector_code_id} onChange={handleFormChange}>
              <option value="">선택 안함</option>
              {sectorCodes.filter(c => c.is_active).map((c) => (
                <option key={c.id} value={c.code_id}>{c.code_name}</option>
              ))}
            </select>
          </div>

          <div className="sf-row">
            <label>장단기</label>
            <select name="term_code_id" value={form.term_code_id} onChange={handleFormChange}>
              <option value="">선택 안함</option>
              {termTypeCodes.filter(c => c.is_active).map((c) => (
                <option key={c.id} value={c.code_id}>{c.code_name}</option>
              ))}
            </select>
          </div>

          <div className="sf-row">
            <label>종목유형 <span className="required">*</span></label>
            <select name="stock_code_id" value={form.stock_code_id} onChange={handleFormChange}>
              <option value="STOCK">일반종목</option>
              <option value="DEPOSIT">예금/적금</option>
              <option value="CMA">CMA</option>
              <option value="ETC">기타</option>
            </select>
          </div>

          <div className="sf-row">
            <label>시세유형 <span className="required">*</span></label>
            <select name="price_code_id" value={form.price_code_id} onChange={handleFormChange}>
              <option value="AUTO">AUTO (자동수집)</option>
              <option value="MANUAL">MANUAL (수동입력)</option>
            </select>
          </div>

          <div className="sf-row">
            <label>
              시장구분
              {form.price_code_id === 'AUTO' && <span className="required"> *</span>}
            </label>
            <select
              name="market_code_id" value={form.market_code_id} onChange={handleFormChange}
              disabled={form.price_code_id === 'MANUAL'}
            >
              <option value="">선택 안함</option>
              <option value="KR">KR - 국내</option>
              <option value="US">US - 미국</option>
            </select>
          </div>

          <div className="sf-row">
            <label>목표수익률</label>
            <div className="sf-input-suffix">
              <input
                type="number" name="target_profit_rate" value={form.target_profit_rate}
                onChange={handleFormChange} min={0} max={999} step={0.01}
                placeholder="예) 5.00"
              />
              <span className="sf-suffix">%</span>
            </div>
          </div>

          <div className="sf-row">
            <label>정렬순서</label>
            <input
              type="number" name="sort_order" value={form.sort_order}
              onChange={handleFormChange} min={0} max={9999}
            />
          </div>

          <div className="sf-row">
            <label>사용유무</label>
            <div className="sf-checkbox">
              <input
                type="checkbox" name="is_active" id="is_active"
                checked={form.is_active} onChange={handleFormChange}
              />
              <label htmlFor="is_active" className="sf-checkbox-label">
                {form.is_active ? '사용' : '미사용'}
              </label>
            </div>
          </div>

          <div className="sf-row">
            <label>
              지표종목
              <span className="sf-help" title="체크 시 지표 비교 영역에 시세가 표시됩니다. 매수 여부와는 무관합니다.">?</span>
            </label>
            <div className="sf-checkbox">
              <input
                type="checkbox" name="is_benchmark" id="is_benchmark"
                checked={form.is_benchmark} onChange={handleFormChange}
              />
              <label htmlFor="is_benchmark" className="sf-checkbox-label">
                {form.is_benchmark ? '지표종목' : '일반'}
              </label>
            </div>
          </div>

          {form.market_code_id === 'KR' && form.stock_code_id === 'STOCK' && (
            <>
              <div className="sf-row">
                <label>
                  비교대상
                  <span className="sf-help" title="건별수익률현황의 2개종목비교 팝업 그래프에 사용됩니다.">?</span>
                </label>
                <select name="compare_type" value={form.compare_type} onChange={handleFormChange}>
                  <option value="">없음</option>
                  <option value="STOCK">종목</option>
                  <option value="INDEX">지수</option>
                </select>
              </div>

              {form.compare_type === 'STOCK' && (
                <div className="sf-row">
                  <label>비교 종목</label>
                  <input
                    list="compare-ticker-options"
                    name="compare_ticker"
                    value={form.compare_ticker}
                    onChange={handleFormChange}
                    placeholder="티커 검색 (예: SPY)"
                    maxLength={20}
                  />
                  <datalist id="compare-ticker-options">
                    {stocks.filter(s => s.id !== editingId).map(s => (
                      <option key={s.id} value={s.ticker}>{s.stock_name}</option>
                    ))}
                  </datalist>
                </div>
              )}

              {form.compare_type === 'INDEX' && (
                <div className="sf-row">
                  <label>비교 지수</label>
                  <select name="compare_index_field" value={form.compare_index_field} onChange={handleFormChange}>
                    <option value="">선택 안함</option>
                    {COMPARE_INDEX_OPTIONS.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
              )}
            </>
          )}

          <div className="sf-actions">
            <button className="btn btn-secondary" onClick={handleNew} disabled={loading}>+ 신규</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={loading} style={{ marginLeft: '8px' }}>
              {loading ? '저장 중...' : '저장'}
            </button>
          </div>
        </div>

      </div>
    </PageLayout>
  );
};

export default StockMasterPage;
